import { useAccept, useAutoPaste } from '@aigc/hooks';
import { InboxOutlined, VideoCameraTwoTone } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { App, Button, Upload } from 'antd';
import { useForm } from 'react-hook-form';
import { object } from 'yup';
import { FormControl } from '../../../components/form-control';
import { Permission } from '../../../features/common/permision';
import {
  createBeforeUpload,
  createUploadValidator,
} from '../../../features/file';
import { usePreviewFn } from '../../../features/file/hooks';
import { fileSchema } from '../../../features/file/schema';
import { TaskType } from '../../../features/task/model';
import { videoQualityRestorationMutation } from '../../../features/video/api';
import { VideoGenerationTaskTable } from '../../../features/video/components/task-table';

export const QualityRestoration = () => {
  const { message, notification } = App.useApp();
  const onPreview = usePreviewFn();

  const form = useForm({
    defaultValues: {},
    resolver: yupResolver(
      object({
        video_file: fileSchema.min(1, '请上传一个视频').required(),
      }),
    ),
  });

  const { control, handleSubmit } = form;

  const videoQuanlityRestoration = useMutation(videoQualityRestorationMutation);

  const handleTranslate = handleSubmit(
    async (values) => {
      const video_url = values.video_file.at(0)?.response?.file_url as string;
      lfq('track', 'video.smart_erase');
      await videoQuanlityRestoration.mutateAsync({
        video_url,
      });

      message.success('视频画质修复任务已提交');
    },
    (error) => {
      console.error(error);
    },
  );

  const accept = useAccept(['video/*']);
  useAutoPaste('#uploader', {
    filter: accept.filter,
  });

  return (
    <div className="flex w-full flex-col divide-y divide-primary-200 overflow-y-auto sm:h-full sm:flex-row sm:divide-x sm:divide-y-0">
      {/* 左右布局，左侧操作面板，占据60% */}
      <div className="card animate-fade-in sm:overflow-auto xl:w-[480px]">
        <div className="space-y-6">
          <FormControl
            control={control}
            name="video_file"
            exact
            render={({ field }) => (
              <Upload.Dragger
                id="uploader"
                ref={field.ref}
                name={field.name}
                disabled={field.disabled}
                classNames={{ list: 'mt-2' }}
                listType="picture"
                fileList={field.value}
                maxCount={1}
                accept={accept}
                iconRender={() => <VideoCameraTwoTone />}
                beforeUpload={createBeforeUpload({
                  validator: createUploadValidator({ accept: accept.format }),
                  onError: ({ title, message }) => {
                    notification.error({ title, description: message });
                  },
                })}
                onPreview={onPreview}
                onChange={(info) => {
                  const { status } = info.file;

                  if (status === 'error') {
                    notification.error({
                      title: '错误',
                      description: `视频 ${info.file.name} 上传失败，请重试`,
                    });
                  }

                  field.onChange(info.fileList);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  点击、拖放或<span className="text-blue-500">粘贴</span>
                  上传视频
                </p>
              </Upload.Dragger>
            )}
          ></FormControl>
          <div className="sticky bottom-0 flex space-x-2">
            <Button
              classNames={{ root: 'sticky bottom-0' }}
              variant="solid"
              color="primary"
              size="large"
              loading={videoQuanlityRestoration.isPending}
              block
              onClick={handleTranslate}
            >
              画质修复
            </Button>
          </div>
        </div>
      </div>

      <div className="card flex-auto animate-slide-up space-y-6 sm:min-h-40 sm:flex-1 sm:overflow-auto">
        <h4 className="text-lg">🎬 视频生成状态</h4>
        <VideoGenerationTaskTable
          taskType={[TaskType.画质修复]}
        ></VideoGenerationTaskTable>
      </div>
    </div>
  );
};

export const Route = createFileRoute(
  '/_creator-center/video/quality-restoration',
)({
  staticData: { permissions: [Permission.画质修复] },
  component: QualityRestoration,
});
